import CloseIcons from '@/icons/CloseIcons'
import ConfirmServiceIcons from '@/icons/ConfirmServiceIcons'
import CopyIcons from '@/icons/CopyIcons'
import LoaderIcons from '@/icons/LoaderIcons'
import {
    InternalTicketWorkflowStatus,
    InternalTicketWorkflowStepName,
    Service,
    TicketWorkflowStep,
} from '@/modules/Service/types'
import { formatDateWithFormatter } from '@/utils/function'
import { useTranslation } from 'react-i18next'
import { Button, Chip, IconButton, Typogaphy } from 'vnetwork-ui'
import CSKHVerificationForm from './CSKHVerificationForm'
import FinanceVerificationForm from './FinanceVerificationForm'
import RegisterService from './RegisterService'
import DownIcons from '@/icons/DownIcons'
import { useInternalTicketWorkflow } from '@/modules/Service/hooks/use-internal-ticket-workflow'

const InternalTicketWorkflow = ({
    serviceId,
    service,
}: {
    serviceId: number
    service: Service
}) => {
    const { t } = useTranslation()
    const {
        expandedStep,
        forceFullView,
        selectedTicketIndex,
        isCopied,
        tickets,
        activeTicket,
        renderState,
        isActiveWorkflow,
        isCSKHVerificationDone,
        progressPercentage,
        handleStepClick,
        handleTicketSelect,
        handleCopyTicketLink,
        handleOpenTicket,
        handleToggleFullView,
        isStepClickable,
        isTicketActive,
    } = useInternalTicketWorkflow(serviceId)

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    if (!tickets || tickets.length === 0 || !activeTicket) {
        return null
    }

    // Compact view for non-active tickets
    if (!isActiveWorkflow) {
        return (
            <div
                className="bg-white border border-gray-200 rounded-[4px] my-2 px-3 py-2 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => handleToggleFullView(true)}
            >
                <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                    <span className="text-xs text-gray-600 font-medium">
                        {t('services:internal_workflow')}
                    </span>
                    <Chip
                        label={`${t(`services:internal_tickets.${activeTicket.ticket_type_key}`) || ''} - ${t(
                            `services:internal_tickets.status.${activeTicket.ticket_status}`
                        )}`}
                        color="default"
                        size="small"
                        className="text-xs rounded-sm"
                        variant="outlined"
                    />
                    {tickets?.length > 1 && (
                        <Chip
                            label={`+${tickets.length - 1}`}
                            color="default"
                            size="small"
                            variant="outlined"
                        />
                    )}
                </div>
                <IconButton size="small">
                    <DownIcons />
                </IconButton>
            </div>
        )
    }

    return (
        <div className="bg-white border border-gray-200 rounded-[4px] my-2 overflow-hidden">
            <div className="px-4 py-2">
                {/* Header */}
                <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse"></div>
                        <Typogaphy className="font-medium text-gray-800 text-xs">
                            {t('services:internal_workflow')}:
                        </Typogaphy>
                        <Chip
                            label={t(
                                `services:internal_tickets.${activeTicket.ticket_type_key}`
                            )}
                            color="info"
                            size="small"
                            variant="outlined"
                        />
                        {tickets?.length > 1 && (
                            <Chip
                                label={`+${tickets.length - 1} ${t('services:internal_tickets.more')}`}
                                color="success"
                                size="small"
                                variant="contained"
                            />
                        )}
                    </div>
                    {forceFullView && (
                        <IconButton
                            size="small"
                            onClick={() => handleToggleFullView(false)}
                        >
                            <CloseIcons />
                        </IconButton>
                    )}
                </div>

                {/* Ticket Selector for multiple tickets */}
                {tickets?.length > 1 && (
                    <div className="mb-2 flex gap-2 overflow-x-auto">
                        {tickets?.map((ticket, index) => (
                            <button
                                key={index}
                                onClick={() => handleTicketSelect(index)}
                                className={`flex-shrink-0 px-4 py-1 rounded-md text-xs border transition-all duration-200 ${
                                    index === selectedTicketIndex
                                        ? 'bg-blue-100 border-blue-300 text-primary'
                                        : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <div
                                        className={`w-2 h-2 rounded-full ${
                                            isTicketActive(
                                                ticket?.ticket_status
                                            )
                                                ? 'bg-success'
                                                : 'bg-gray-400'
                                        }`}
                                    />
                                    <span>
                                        {t(
                                            `services:internal_tickets.${ticket.ticket_type_key}`
                                        )}
                                    </span>
                                    <span className="text-gray-500">
                                        (
                                        {t(
                                            `services:internal_tickets.status.${ticket.ticket_status}`
                                        )}
                                        )
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>
                )}

                {/* Progress Steps */}
                <div className="flex items-start justify-between relative px-3 bg-gray-50 rounded-md py-3">
                    {/* Progress Line */}
                    <div className="absolute top-[24px] left-10 right-10 h-px bg-gray-300">
                        <div
                            className="h-full bg-primary transition-all duration-500"
                            style={{ width: `${progressPercentage}%` }}
                        />
                    </div>

                    {activeTicket.ticket_workflow_steps
                        ?.sort((a: any, b: any) => a.step_order - b.step_order)
                        .map((step: TicketWorkflowStep, index: number) => (
                            <StepItem
                                key={step.step_id}
                                step={step}
                                index={index}
                                expandedStep={expandedStep}
                                isClickable={isStepClickable(step.status)}
                                onStepClick={handleStepClick}
                                t={t}
                            />
                        ))}
                </div>

                {/* Copy Ticket Link & View Progress Buttons */}
                {isCSKHVerificationDone && activeTicket?.ticket_id && (
                    <div className="flex justify-start gap-2 mt-2">
                        <Button
                            size="small"
                            variant="outlined"
                            color={isCopied ? 'success' : 'primary'}
                            onClick={handleCopyTicketLink}
                        >
                            <CopyIcons className="w-3 h-3 mr-1" />
                            {isCopied
                                ? t('copied')
                                : t(
                                      'services:internal_tickets.copy_ticket_link'
                                  )}
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={handleOpenTicket}
                        >
                            {t('services:internal_tickets.view_progress')}
                        </Button>
                    </div>
                )}

                {/* Step Details */}
                {expandedStep && (
                    <StepDetails
                        steps={activeTicket.ticket_workflow_steps}
                        expandedStep={expandedStep}
                        service={service}
                        t={t}
                    />
                )}
            </div>
        </div>
    )
}

// Step Item Component
interface StepItemProps {
    step: TicketWorkflowStep
    index: number
    expandedStep: number | null
    isClickable: boolean
    onStepClick: (stepId: number) => void
    t: (key: string) => string
}

const StepItem = ({
    step,
    index,
    isClickable,
    onStepClick,
    t,
}: StepItemProps) => {
    const getStatusClass = () => {
        switch (step.status) {
            case InternalTicketWorkflowStatus.Done:
                return 'bg-primary border-primary text-white cursor-pointer'
            case InternalTicketWorkflowStatus.Doing:
                return 'bg-white border-primary text-primary cursor-pointer'
            case InternalTicketWorkflowStatus.Blocked:
                return 'bg-red-500 border-red-500 text-white cursor-pointer'
            default:
                return 'bg-white border-gray-300 text-gray-400 cursor-not-allowed'
        }
    }

    const getLabelClass = () => {
        switch (step.status) {
            case InternalTicketWorkflowStatus.Done:
                return 'text-primary'
            case InternalTicketWorkflowStatus.Doing:
                return 'text-primary font-semibold'
            case InternalTicketWorkflowStatus.Blocked:
                return 'text-red-600'
            default:
                return 'text-gray-500'
        }
    }

    const getStatusTextClass = () => {
        switch (step.status) {
            case InternalTicketWorkflowStatus.Done:
                return 'text-gray-400'
            case InternalTicketWorkflowStatus.Doing:
                return 'text-primary'
            case InternalTicketWorkflowStatus.Blocked:
                return 'text-red-500'
            default:
                return 'text-gray-400'
        }
    }

    const getStatusText = () => {
        switch (step.status) {
            case InternalTicketWorkflowStatus.Done:
                return t('completed')
            case InternalTicketWorkflowStatus.Doing:
                return t('processing')
            case InternalTicketWorkflowStatus.Blocked:
                return t('blocked')
            default:
                return t('pending')
        }
    }

    const renderStepIcon = () => {
        switch (step.status) {
            case InternalTicketWorkflowStatus.Done:
                return <ConfirmServiceIcons className="w-3 h-3" />
            case InternalTicketWorkflowStatus.Doing:
                return <LoaderIcons className="w-3 h-3 animate-spin" />
            case InternalTicketWorkflowStatus.Blocked:
                return <span>!</span>
            default:
                return <span>{index + 1}</span>
        }
    }

    return (
        <div className="flex flex-col items-center relative z-10 flex-1">
            {/* Step Circle */}
            <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium border ${getStatusClass()}`}
                onClick={() => isClickable && onStepClick(step.step_id)}
            >
                {renderStepIcon()}
            </div>

            <div className="mt-1.5 text-center max-w-[100px]">
                <div
                    className={`text-[10px] font-medium leading-tight ${getLabelClass()}`}
                >
                    {t(
                        `services:internal_tickets.actions.${step.step_name.toLowerCase()}`
                    )}
                </div>
                <div className={`text-[9px] mt-0.5 ${getStatusTextClass()}`}>
                    {getStatusText()}
                </div>
            </div>
        </div>
    )
}

// Step Details Component
interface StepDetailsProps {
    steps: TicketWorkflowStep[]
    expandedStep: number
    service: Service
    t: (key: string) => string
}

const StepDetails = ({ steps, expandedStep, service, t }: StepDetailsProps) => {
    const step = steps.find((s) => s.step_id === expandedStep)
    if (!step) return null

    const getDotClass = () => {
        switch (step.status) {
            case InternalTicketWorkflowStatus.Done:
            case InternalTicketWorkflowStatus.Doing:
                return 'bg-primary'
            case InternalTicketWorkflowStatus.Blocked:
                return 'bg-red-500'
            default:
                return 'bg-gray-300'
        }
    }

    const showCSKHForm = [
        InternalTicketWorkflowStepName.CSKHConfirm.toLowerCase(),
        InternalTicketWorkflowStepName.CSKHVerifyCreateService.toLowerCase(),
        InternalTicketWorkflowStepName.CSKHConfirmCancel.toLowerCase(),
        InternalTicketWorkflowStepName.FinalDone.toLowerCase(),
        InternalTicketWorkflowStepName.CSKHConfirmTaxPercentageUpdate.toLowerCase(),
        InternalTicketWorkflowStepName.CSKHXacNhan.toLowerCase(),
        InternalTicketWorkflowStepName.CSKHKiemTraHoanTat.toLowerCase(),
        // Note: TechKhoiTaoTrenDC uses RegisterService, not CSKHVerificationForm
    ].includes(step.step_name?.toLowerCase())

    return (
        <div className="mt-2">
            <div className="bg-gray-50 rounded-md p-3">
                <div className="flex items-center gap-1.5 mb-2">
                    <div
                        className={`w-1.5 h-1.5 rounded-full ${getDotClass()}`}
                    ></div>
                    <Typogaphy className="font-medium text-primary text-xs">
                        {t(
                            `services:internal_tickets.actions.${step.step_name.toLowerCase()}`
                        )}
                    </Typogaphy>
                </div>

                {/* Technical Setup Form */}
                {[
                    InternalTicketWorkflowStepName.TechnicalCreateService.toLowerCase(),
                    InternalTicketWorkflowStepName.TechKhoiTaoTrenDC.toLowerCase(),
                ].includes(step.step_name?.toLowerCase()) &&
                    step.status?.toLowerCase() ===
                        InternalTicketWorkflowStatus.Doing.toLowerCase() && (
                        <RegisterService
                            service={service}
                            stepId={step.step_id}
                        />
                    )}

                {/* Technical Cancel Service Form */}
                {step.step_name?.toLowerCase() ===
                    InternalTicketWorkflowStepName.TechnicalCancelService.toLowerCase() &&
                    step.status?.toLowerCase() ===
                        InternalTicketWorkflowStatus.Doing.toLowerCase() && (
                        <CSKHVerificationForm
                            stepId={step.step_id}
                            stepName={step.step_name}
                        />
                    )}

                {/* CSKH Verification Forms */}
                {showCSKHForm &&
                    step.status?.toLowerCase() ===
                        InternalTicketWorkflowStatus.Doing.toLowerCase() && (
                        <CSKHVerificationForm
                            stepId={step.step_id}
                            stepName={step.step_name}
                            service={service}
                        />
                    )}

                {/* Finance Verification Form */}
                {step.step_name?.toLowerCase() ===
                    InternalTicketWorkflowStepName.FinanceApproveTaxPercentageUpdate.toLowerCase() &&
                    step.status?.toLowerCase() ===
                        InternalTicketWorkflowStatus.Doing.toLowerCase() && (
                        <FinanceVerificationForm
                            stepId={step.step_id}
                            stepName={step.step_name}
                            filledData={step.filled_data}
                        />
                    )}

                {/* Approver Info */}
                {step.approver_name && (
                    <div className="mt-2 text-xs text-gray-500 space-y-1">
                        <div>
                            {t('services:internal_tickets.approver_name')}:{' '}
                            {step.approver_name}
                        </div>
                        <div>
                            {t('services:internal_tickets.approver_at')}:{' '}
                            {formatDateWithFormatter(
                                step.approved_at,
                                'DD/MM/YYYY HH:mm'
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default InternalTicketWorkflow
